import React, {useState, useCallback} from 'react';
import {Button, BodySmall, Card, Textarea} from '@spaced-out/ui-design-system';
import {classify} from 'src/utils/classify';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import css from './message-feedback.css';


export type MessageFeedbackProps = {
  messageId: string;
  savedRating?: number;
  savedComment?: string;
  onSubmit: (
    messageId: string,
    rating: number,
    comment: string,
  ) => Promise<void>;
};

export function MessageFeedback({
  messageId,
  savedRating,
  savedComment,
  onSubmit,
}: MessageFeedbackProps) {
  const [selectedRating, setSelectedRating] = useState<number | undefined>(
    savedRating,
  );
  const [formOpen, setFormOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleThumbClick = useCallback((rating: number) => {
    setSelectedRating(rating);
    setFormOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (comment: string) => {
      setSubmitting(true);
      if (selectedRating == null) {
        setSubmitting(false);
        return;
      }

      try {
        await onSubmit(messageId, selectedRating, comment);
        setFormOpen(false);
        setSubmitting(false);
      } catch (error) {
      } finally {
        setSubmitting(false);
      }
    },
    [messageId, selectedRating, onSubmit, setSubmitting],
  );

  const closeForm = useCallback(() => {
    setFormOpen(false);
  }, []);

  return (
    <div className={css.wrapper}>
      <div className={css.thumbs}>
        <div
          className={classify({
            [css.thumbButtonSelected]: savedRating === 1,
            [css.thumbButton]: savedRating !== 1,
          })}
        >
          <Button
            type="ghost"
            size="small"
            iconLeftName="thumbs-up"
            iconLeftType={savedRating === 1 ? 'solid' : 'regular'}
            onClick={() => handleThumbClick(1)}
          />
        </div>
        <div
          className={classify({
            [css.thumbButtonSelected]: savedRating === -1,
            [css.thumbButton]: savedRating !== -1,
          })}
        >
          <Button
            type="ghost"
            size="small"
            iconLeftName="thumbs-down"
            iconLeftType={savedRating === -1 ? 'solid' : 'regular'}
            onClick={() => handleThumbClick(-1)}
          />
        </div>
      </div>

      {formOpen && (
        <MessageFeedbackForm
          submitting={submitting}
          savedComment={savedComment ?? ''}
          onSubmit={handleSubmit}
          closeForm={closeForm}
        />
      )}
    </div>
  );
}

type MessageFeedbackFormProps = {
  savedComment: string;
  onSubmit: (comment: string) => Promise<void>;
  closeForm: () => void;
  submitting: boolean;
};
function MessageFeedbackForm({
  submitting,
  savedComment,
  onSubmit,
  closeForm,
}: MessageFeedbackFormProps) {
  const {t} = useLabelTranslation();

  const [comment, setComment] = useState(savedComment);

  return (
    <Card classNames={{wrapper: css.formCard}}>
      <BodySmall>
        {t('Tell_us_more_Optional', 'Tell us more (Optional)')}
      </BodySmall>
      <Textarea
        placeholder={t('Write_here', 'Write here...')}
        value={comment}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setComment(e.target.value)
        }
      />
      <div className={css.actions}>
        <Button
          type="secondary"
          size="small"
          onClick={closeForm}
          disabled={submitting}
        >
          {t('Cancel', 'Cancel')}
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={() => onSubmit(comment)}
          disabled={submitting}
        >
          {t('Submit_feedback', 'Submit feedback')}
        </Button>
      </div>
    </Card>
  );
}
